import type { DrinkEntry } from '@/types'
import { ETHANOL_DENSITY } from './bac'

export type Metric = 'bac' | 'drinks' | 'calories' | 'ml'

export interface DailyAggregate {
  date: string        // YYYY-MM-DD in local time
  peakBac: number     // highest BAC reached on that day
  drinks: number      // count of non-gap entries
  calories: number    // sum
  ml: number          // sum
  stdDrinks: number   // Australian standard drinks (10g ethanol = 1 std drink)
}

function localDateKey(iso: string): string {
  const d = new Date(iso)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

export function computeDailyAggregates(entries: DrinkEntry[]): DailyAggregate[] {
  const map = new Map<string, DailyAggregate>()
  for (const e of entries) {
    if (e.is_gap) continue
    const key = localDateKey(e.finished_at)
    const ml = Number(e.drink.milliliters) || 0
    const abv = Number(e.drink.alcohol) || 0
    const cal = Number(e.drink.calories) || 0
    const std = (ml * (abv / 100) * ETHANOL_DENSITY) / 10
    const bac = Number(e.bac) || 0

    let agg = map.get(key)
    if (!agg) {
      agg = { date: key, peakBac: 0, drinks: 0, calories: 0, ml: 0, stdDrinks: 0 }
      map.set(key, agg)
    }
    agg.drinks += 1
    agg.calories += cal
    agg.ml += ml
    agg.stdDrinks += std
    if (bac > agg.peakBac) agg.peakBac = bac
  }
  return [...map.values()].sort((a, b) => (a.date < b.date ? -1 : 1))
}

export function getMetricValue(agg: DailyAggregate, metric: Metric): number {
  switch (metric) {
    case 'bac': return agg.peakBac
    case 'drinks': return agg.drinks
    case 'calories': return agg.calories
    case 'ml': return agg.ml
  }
}

export interface MetricSpec {
  label: string
  shortLabel: string
  format: (v: number) => string
  // Lower bound (inclusive) of each bucket. Length === colors.length.
  buckets: [number, number, number, number, number]
  // Color for each bucket level (1..5). Empty cells (value 0) use a neutral background.
  colors: [string, string, string, string, string]
  // Human-readable range label for each bucket level (1..5).
  bucketLabels: [string, string, string, string, string]
}

// Green → red ramp. Empty days stay neutral (handled by the heatmap component).
const RAMP: [string, string, string, string, string] = [
  '#15803d', // green-700
  '#84cc16', // lime-500
  '#eab308', // yellow-500
  '#f97316', // orange-500
  '#dc2626', // red-600
]

export const METRIC_SPECS: Record<Metric, MetricSpec> = {
  bac: {
    label: 'Peak BAC',
    shortLabel: 'BAC',
    format: v => v.toFixed(3) + '%',
    // Aligns with bac.ts status thresholds: safe < 0.01, low < 0.05, caution < 0.10, danger < 0.20, critical >= 0.20
    buckets: [0.001, 0.01, 0.05, 0.10, 0.20],
    colors: RAMP,
    bucketLabels: [
      'Trace (<0.01%)',
      'Low (0.01–0.05%)',
      'Caution (0.05–0.10%)',
      'Danger (0.10–0.20%)',
      'Critical (≥0.20%)',
    ],
  },
  drinks: {
    label: 'Drinks',
    shortLabel: 'Drinks',
    format: v => `${v} drink${v === 1 ? '' : 's'}`,
    buckets: [1, 2, 4, 6, 10],
    colors: RAMP,
    bucketLabels: ['1 drink', '2–3 drinks', '4–5 drinks', '6–9 drinks', '10+ drinks'],
  },
  calories: {
    label: 'Calories',
    shortLabel: 'Calories',
    format: v => `${Math.round(v)} kcal`,
    buckets: [1, 150, 400, 800, 1500],
    colors: RAMP,
    bucketLabels: ['<150 kcal', '150–399', '400–799', '800–1499', '1500+'],
  },
  ml: {
    label: 'Volume',
    shortLabel: 'mL',
    format: v => `${Math.round(v)} mL`,
    buckets: [1, 350, 800, 1500, 3000],
    colors: RAMP,
    bucketLabels: ['<350 mL', '350–799', '800–1499', '1500–2999', '3000+'],
  },
}

export function getBucketLevel(value: number, buckets: readonly number[]): number {
  if (value <= 0) return 0
  let level = 0
  for (let i = 0; i < buckets.length; i++) {
    if (value >= buckets[i]) level = i + 1
  }
  return level
}

// ───────── Period filtering ─────────

export type Period = '1mo' | '3mo' | '6mo' | '12mo' | 'all'

export interface PeriodOption {
  id: Period
  label: string
  days: number | null   // null = all-time
}

export const PERIOD_OPTIONS: PeriodOption[] = [
  { id: '1mo',  label: '1 mo',  days: 30 },
  { id: '3mo',  label: '3 mo',  days: 90 },
  { id: '6mo',  label: '6 mo',  days: 180 },
  { id: '12mo', label: '12 mo', days: 365 },
  { id: 'all',  label: 'All',   days: null },
]

function cutoffFor(period: Period): number | null {
  const opt = PERIOD_OPTIONS.find(p => p.id === period)
  if (!opt || opt.days === null) return null
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - opt.days)
  cutoff.setHours(0, 0, 0, 0)
  return cutoff.getTime()
}

// Resolves the start of the visible period to an actual Date. For 'all' this is
// the earliest entry date; for fixed windows it's `now - days`.
export function periodStartDate(aggregates: DailyAggregate[], period: Period): Date {
  const cutoff = cutoffFor(period)
  if (cutoff !== null) return new Date(cutoff)
  if (aggregates.length === 0) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  }
  return new Date(aggregates[0].date + 'T00:00:00')
}

export function filterEntriesByPeriod(entries: DrinkEntry[], period: Period): DrinkEntry[] {
  const cutoff = cutoffFor(period)
  if (cutoff === null) return entries
  return entries.filter(e => new Date(e.finished_at).getTime() >= cutoff)
}

export function filterAggregatesByPeriod(aggregates: DailyAggregate[], period: Period): DailyAggregate[] {
  const cutoff = cutoffFor(period)
  if (cutoff === null) return aggregates
  return aggregates.filter(a => new Date(a.date + 'T00:00:00').getTime() >= cutoff)
}

// Number of calendar days the period spans (clamped to time since first aggregate for 'all').
export function periodDayCount(aggregates: DailyAggregate[], period: Period): number {
  const opt = PERIOD_OPTIONS.find(p => p.id === period)
  if (opt && opt.days !== null) return opt.days
  if (aggregates.length === 0) return 0
  const first = new Date(aggregates[0].date + 'T00:00:00').getTime()
  return Math.max(1, Math.ceil((Date.now() - first) / (1000 * 60 * 60 * 24)))
}

// ───────── Summary stats ─────────

export interface SummaryStats {
  totalDrinks: number
  totalStdDrinks: number
  totalCalories: number
  totalMl: number
  drinkingDays: number
  soberDays: number
  soberDayPercent: number    // 0-100
  avgPeakBacDrinkingDays: number
  avgDrinksDrinkingDays: number
  avgStdDrinksPerWeek: number
  maxPeakBac: number
  maxDrinksInDay: number
}

export function computeSummaryStats(
  aggregates: DailyAggregate[],
  totalDayCount: number
): SummaryStats {
  const drinkingDays = aggregates.length
  const soberDays = Math.max(0, totalDayCount - drinkingDays)
  const totalDrinks = aggregates.reduce((s, a) => s + a.drinks, 0)
  const totalStdDrinks = aggregates.reduce((s, a) => s + a.stdDrinks, 0)
  const totalCalories = aggregates.reduce((s, a) => s + a.calories, 0)
  const totalMl = aggregates.reduce((s, a) => s + a.ml, 0)
  const sumPeakBac = aggregates.reduce((s, a) => s + a.peakBac, 0)
  const maxPeakBac = aggregates.reduce((m, a) => Math.max(m, a.peakBac), 0)
  const maxDrinksInDay = aggregates.reduce((m, a) => Math.max(m, a.drinks), 0)
  const avgPeakBacDrinkingDays = drinkingDays > 0 ? sumPeakBac / drinkingDays : 0
  const avgDrinksDrinkingDays = drinkingDays > 0 ? totalDrinks / drinkingDays : 0
  const weeks = Math.max(1, totalDayCount / 7)
  return {
    totalDrinks,
    totalStdDrinks,
    totalCalories,
    totalMl,
    drinkingDays,
    soberDays,
    soberDayPercent: totalDayCount > 0 ? (soberDays / totalDayCount) * 100 : 0,
    avgPeakBacDrinkingDays,
    avgDrinksDrinkingDays,
    avgStdDrinksPerWeek: totalStdDrinks / weeks,
    maxPeakBac,
    maxDrinksInDay,
  }
}

// ───────── Weekly standard drinks (ISO weeks, Mon-Sun) ─────────

export interface WeeklyBucket {
  weekStart: string    // YYYY-MM-DD of the Monday
  stdDrinks: number
  drinks: number
}

function startOfISOWeek(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const day = (out.getDay() + 6) % 7 // Mon=0
  out.setDate(out.getDate() - day)
  return out
}

function dateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

export function computeWeeklyStdDrinks(
  aggregates: DailyAggregate[],
  rangeStart: Date,
  rangeEnd: Date = new Date()
): WeeklyBucket[] {
  const map = new Map<string, WeeklyBucket>()
  for (const a of aggregates) {
    const d = new Date(a.date + 'T00:00:00')
    const ws = dateKey(startOfISOWeek(d))
    let b = map.get(ws)
    if (!b) {
      b = { weekStart: ws, stdDrinks: 0, drinks: 0 }
      map.set(ws, b)
    }
    b.stdDrinks += a.stdDrinks
    b.drinks += a.drinks
  }
  // Walk every week from rangeStart's week through rangeEnd's week so dry
  // spells at either end stay visible.
  const out: WeeklyBucket[] = []
  const startWeek = startOfISOWeek(rangeStart)
  const endWeek = startOfISOWeek(rangeEnd)
  const cursor = new Date(startWeek)
  while (cursor.getTime() <= endWeek.getTime()) {
    const key = dateKey(cursor)
    out.push(map.get(key) ?? { weekStart: key, stdDrinks: 0, drinks: 0 })
    cursor.setDate(cursor.getDate() + 7)
  }
  return out
}

// ───────── Day-of-week pattern ─────────

// Returns 7 entries, Mon..Sun. avgDrinks = total drinks on that weekday / count of that weekday in the period.
export interface DayOfWeekStat {
  day: number     // 0 = Mon ... 6 = Sun
  label: string
  totalDrinks: number
  totalStdDrinks: number
  occurrences: number       // count of that weekday in the filtered range (including zero-drink days)
  drinkingOccurrences: number  // count of that weekday with at least 1 drink
  avgDrinks: number
  avgStdDrinks: number
}

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export function computeDayOfWeekStats(
  aggregates: DailyAggregate[],
  totalDayCount: number
): DayOfWeekStat[] {
  const stats: DayOfWeekStat[] = DOW_LABELS.map((label, day) => ({
    day, label, totalDrinks: 0, totalStdDrinks: 0, occurrences: 0,
    drinkingOccurrences: 0, avgDrinks: 0, avgStdDrinks: 0,
  }))
  // Count drinks per weekday from aggregates
  for (const a of aggregates) {
    const d = new Date(a.date + 'T00:00:00')
    const dow = (d.getDay() + 6) % 7
    stats[dow].totalDrinks += a.drinks
    stats[dow].totalStdDrinks += a.stdDrinks
    if (a.drinks > 0) stats[dow].drinkingOccurrences += 1
  }
  // Count occurrences of each weekday in the trailing window
  if (totalDayCount > 0) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const start = new Date(today)
    start.setDate(today.getDate() - (totalDayCount - 1))
    const cursor = new Date(start)
    while (cursor.getTime() <= today.getTime()) {
      const dow = (cursor.getDay() + 6) % 7
      stats[dow].occurrences += 1
      cursor.setDate(cursor.getDate() + 1)
    }
  }
  for (const s of stats) {
    if (s.occurrences > 0) {
      s.avgDrinks = s.totalDrinks / s.occurrences
      s.avgStdDrinks = s.totalStdDrinks / s.occurrences
    }
  }
  return stats
}

// ───────── Time-of-day pattern ─────────

export interface HourStat {
  hour: number       // 0..23
  drinks: number
}

export function computeTimeOfDayStats(entries: DrinkEntry[]): HourStat[] {
  const stats: HourStat[] = Array.from({ length: 24 }, (_, hour) => ({ hour, drinks: 0 }))
  for (const e of entries) {
    if (e.is_gap) continue
    const d = new Date(e.finished_at)
    stats[d.getHours()].drinks += 1
  }
  return stats
}

// ───────── Top drinks ─────────

export interface TopDrink {
  name: string
  count: number
  totalMl: number
  totalStdDrinks: number
  totalCalories: number
}

export function computeTopDrinks(entries: DrinkEntry[], limit = 10): TopDrink[] {
  const map = new Map<string, TopDrink>()
  for (const e of entries) {
    if (e.is_gap) continue
    const name = e.drink.drink
    const ml = Number(e.drink.milliliters) || 0
    const abv = Number(e.drink.alcohol) || 0
    const cal = Number(e.drink.calories) || 0
    const std = (ml * (abv / 100) * ETHANOL_DENSITY) / 10
    let t = map.get(name)
    if (!t) {
      t = { name, count: 0, totalMl: 0, totalStdDrinks: 0, totalCalories: 0 }
      map.set(name, t)
    }
    t.count += 1
    t.totalMl += ml
    t.totalStdDrinks += std
    t.totalCalories += cal
  }
  return [...map.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}
